import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Layers, ArrowLeft, Save, AlertTriangle, CheckCircle, Info,
  Users, Sparkles, BookOpen, ChevronRight, Loader,
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import {
  useLazyGetPropositionMutualiseeQuery,
  useAppliquerRepartitionMutation,
  PropositionUE,
  PropositionMatiere,
} from '../../services/uesApi';

const VueMutualisee: React.FC = () => {
  const [sp] = useSearchParams();
  const navigate = useNavigate();

  const niveau = sp.get('niveau') || 'L1';
  const semestre = Number(sp.get('semestre') || '1');
  const optionsIds = (sp.get('options_ids') || '').split(',').map(Number).filter(Boolean);

  const [fetchMutualisee, { data, isFetching, error }] = useLazyGetPropositionMutualiseeQuery();
  const [appliquer, { isLoading: isSaving }] = useAppliquerRepartitionMutation();
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // UEs locales éditables (tronc commun)
  const [ues, setUes] = useState<PropositionUE[]>([]);
  const [activeTab, setActiveTab] = useState<'commun' | number>('commun');

  useEffect(() => {
    if (optionsIds.length >= 2 && niveau && semestre) {
      fetchMutualisee({ niveau, semestre, options_ids: optionsIds });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [niveau, semestre, optionsIds.join(',')]);

  // Initialiser les UEs depuis la proposition backend
  useEffect(() => {
    if (data?.tronc_commun?.ues) {
      setUes(data.tronc_commun.ues);
    }
  }, [data]);

  const stats = useMemo(() => {
    const totalCredits = ues.reduce((s, u) =>
      s + u.matieres.reduce((ss, m) => ss + m.credits, 0), 0);
    return {
      totalCredits: Math.round(totalCredits * 100) / 100,
      nbUes: ues.length,
      nbMatieres: ues.reduce((s, u) => s + u.matieres.length, 0),
    };
  }, [ues]);

  // Appliquer à TOUTES les options du groupe
  const handleAppliquerAToutes = async () => {
    if (!data) return;
    if (!window.confirm(
      `Appliquer cette répartition aux ${data.options.length} options en même temps ?\n\n` +
      `${data.options.map(o => '- ' + o.nom).join('\n')}\n\n` +
      `Chaque option recevra les mêmes UE pour le tronc commun. ` +
      `Les matières spécifiques restantes seront à placer manuellement.`
    )) return;

    setMessage(null);
    let succes = 0, erreurs = 0;
    for (const opt of data.options) {
      try {
        const payload = {
          niveau,
          semestre,
          option_id: opt.id,
          ues: ues.map((ue, i) => ({
            code_ue: ue.code_ue.replace(/-[^-]+$/, `-${opt.code}`), // adapte le code par option
            nom_ue: ue.nom_ue,
            ordre_affichage: i + 1,
            matiere_ids: ue.matieres.map(m => m.id),
          })),
        };
        await appliquer(payload as any).unwrap();
        succes++;
      } catch {
        erreurs++;
      }
    }
    setMessage({
      type: erreurs === 0 ? 'success' : 'error',
      text: `Répartition appliquée : ${succes} succès, ${erreurs} erreur(s). Il reste à placer les matières spécifiques de chaque option.`,
    });
  };

  if (optionsIds.length < 2) {
    return (
      <Card>
        <div className="text-center py-12">
          <AlertTriangle size={48} className="mx-auto text-amber-500 mb-3" />
          <p className="text-gray-700 dark:text-gray-300">
            La vue mutualisée nécessite au moins 2 options.
          </p>
          <Button variant="secondary" onClick={() => navigate('/repartition-ue')} className="mt-4">
            <ArrowLeft size={14} className="mr-1" /> Retour à la vue standard
          </Button>
        </div>
      </Card>
    );
  }

  if (isFetching) {
    return <div className="flex justify-center py-20"><Spinner size="lg" /></div>;
  }

  if (error || !data) {
    return (
      <Card>
        <div className="text-center py-12">
          <AlertTriangle size={40} className="mx-auto text-red-500 mb-3" />
          <p className="text-red-700 dark:text-red-300">
            Impossible de charger la proposition mutualisée.
          </p>
          <Button variant="secondary" onClick={() => navigate('/repartition-ue')} className="mt-3">
            Retour
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <button
              onClick={() => navigate('/repartition-ue')}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <ArrowLeft size={18} />
            </button>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Sparkles size={26} className="text-purple-600" />
              Répartition mutualisée
            </h1>
          </div>
          <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm">
            {data.options.length} options, {niveau} S{semestre} — tronc commun de {data.options[0]?.nb_matieres_communes ?? 0} matières
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="primary"
            size="sm"
            icon={<Save size={16} />}
            onClick={handleAppliquerAToutes}
            disabled={ues.length === 0 || isSaving}
          >
            {isSaving ? 'Application...' : `Appliquer aux ${data.options.length} options`}
          </Button>
        </div>
      </div>

      {message && (
        <div className={`p-3 rounded-lg border text-sm flex items-center gap-2 ${
          message.type === 'success'
            ? 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-700 dark:text-green-300'
            : 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-700 dark:text-red-300'
        }`}>
          {message.type === 'success' ? <CheckCircle size={14} /> : <AlertTriangle size={14} />}
          {message.text}
        </div>
      )}

      {/* Stats rapides */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-700">
          <div className="text-xs text-purple-600 dark:text-purple-400 flex items-center gap-1">
            <Users size={11} /> Options
          </div>
          <div className="text-xl font-bold text-purple-700 dark:text-purple-300">{data.options.length}</div>
        </div>
        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
          <div className="text-xs text-blue-600 dark:text-blue-400">Matières communes</div>
          <div className="text-xl font-bold text-blue-700 dark:text-blue-300">
            {data.options[0]?.nb_matieres_communes ?? 0}
          </div>
        </div>
        <div className={`p-3 rounded-lg border ${
          Math.abs(stats.totalCredits - 30) < 0.01
            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700'
            : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700'
        }`}>
          <div className="text-xs">Crédits du tronc commun</div>
          <div className="text-xl font-bold">{stats.totalCredits}</div>
        </div>
        <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-700">
          <div className="text-xs text-orange-600 dark:text-orange-400">Spécifiques (total)</div>
          <div className="text-xl font-bold text-orange-700 dark:text-orange-300">
            {data.options.reduce((s, o) => s + (o.nb_matieres_specifiques ?? 0), 0)}
          </div>
        </div>
      </div>

      {/* Liste des options */}
      <Card>
        <h3 className="text-sm font-semibold mb-2 flex items-center gap-1 text-gray-700 dark:text-gray-300">
          <BookOpen size={14} /> Options du groupe mutualisé
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {data.options.map(opt => (
            <div key={opt.id} className="p-2 border border-gray-200 dark:border-gray-700 rounded">
              <div className="font-medium text-sm text-gray-800 dark:text-gray-200">
                {opt.code} — {opt.nom}
              </div>
              <div className="text-[11px] text-gray-500 dark:text-gray-400">
                {opt.pole_nom} · {opt.nb_matieres_total} matières total
                ({opt.nb_matieres_communes} communes, {opt.nb_matieres_specifiques} spécifiques)
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Onglets Tronc commun / Matières spécifiques par option */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-0 -mb-px overflow-x-auto">
          <button
            onClick={() => setActiveTab('commun')}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap ${
              activeTab === 'commun'
                ? 'border-purple-600 text-purple-600 dark:border-purple-400 dark:text-purple-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            <Sparkles size={14} />
            Tronc commun ({ues.length} UE)
          </button>
          {data.options.map(opt => {
            const specs = data.matieres_specifiques_par_option[String(opt.id)] || [];
            return (
              <button
                key={opt.id}
                onClick={() => setActiveTab(opt.id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap ${
                  activeTab === opt.id
                    ? 'border-orange-600 text-orange-600 dark:border-orange-400 dark:text-orange-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
                }`}
              >
                {opt.code} <span className="text-[10px] opacity-70">({specs.length} spéc.)</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Contenu onglet actif */}
      {activeTab === 'commun' ? (
        <div className="space-y-3">
          <div className="flex items-start gap-2 p-2.5 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded text-xs text-purple-800 dark:text-purple-300">
            <Info size={14} className="mt-0.5" />
            <span>
              Voici la proposition pour les matières <strong>communes</strong> à toutes les options.
              Cette répartition sera appliquée à chaque option. Les matières spécifiques à chaque
              option (onglets à droite) devront ensuite être ajoutées manuellement via la vue standard.
            </span>
          </div>
          {ues.map((ue, idx) => (
            <UEReadOnlyCard key={idx} ue={ue} />
          ))}
          {ues.length === 0 && (
            <Card>
              <div className="text-center py-12 text-gray-400">
                <BookOpen size={40} className="mx-auto mb-3 opacity-30" />
                <p>Aucune matière commune trouvée. Les options ont probablement trop de différences.</p>
              </div>
            </Card>
          )}
        </div>
      ) : (
        <MatieresSpecifiquesPane
          option={data.options.find(o => o.id === activeTab)!}
          matieres={data.matieres_specifiques_par_option[String(activeTab)] || []}
        />
      )}
    </div>
  );
};

// ─── Sous-composants ──────────────────────────────────────────────────────────

const UEReadOnlyCard: React.FC<{ ue: PropositionUE }> = ({ ue }) => {
  const cr = ue.matieres.reduce((s, m) => s + m.credits, 0);
  const ok = cr >= 5 && cr <= 10;
  return (
    <Card className={ue.est_tp ? 'border-orange-300 dark:border-orange-700 bg-orange-50/30 dark:bg-orange-900/10' : ''}>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="px-2 py-0.5 bg-blue-600 text-white rounded text-xs font-medium">UE</span>
          <span className="font-mono text-xs text-gray-500">{ue.code_ue}</span>
          <span className="font-semibold text-gray-800 dark:text-gray-200">{ue.nom_ue}</span>
          {ue.est_tp && (
            <span className="text-[10px] uppercase px-1.5 py-0.5 bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300 rounded">TP</span>
          )}
        </div>
        <div className={`text-sm font-bold ${ok ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>
          {cr.toFixed(1)} cr · {ue.matieres.length} matières
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {ue.matieres.map(m => (
          <span
            key={m.id}
            className="px-2 py-1 text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded"
          >
            <span className="font-medium">{m.nom_matiere}</span>
            <span className="opacity-60 ml-1">· {m.credits} cr</span>
          </span>
        ))}
      </div>
    </Card>
  );
};

const MatieresSpecifiquesPane: React.FC<{
  option: any;
  matieres: PropositionMatiere[];
}> = ({ option, matieres }) => {
  if (matieres.length === 0) {
    return (
      <Card>
        <div className="text-center py-10 text-gray-400">
          <CheckCircle size={36} className="mx-auto mb-2 opacity-40" />
          <p>Aucune matière spécifique à <strong>{option.code}</strong>. Toutes ses matières sont dans le tronc commun.</p>
        </div>
      </Card>
    );
  }
  return (
    <Card>
      <div className="flex items-start gap-2 p-2.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded text-xs text-amber-800 dark:text-amber-300 mb-3">
        <Info size={14} className="mt-0.5" />
        <span>
          Voici les matières <strong>spécifiques</strong> à {option.code} qui ne sont dans aucune autre option du groupe.
          Après avoir appliqué le tronc commun, utilise la vue standard (/repartition-ue) pour placer ces matières dans une UE propre à cette option.
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        {matieres.map(m => (
          <div
            key={m.id}
            className="px-3 py-1.5 text-sm bg-white dark:bg-gray-800 border border-amber-200 dark:border-amber-700 rounded flex items-center gap-2"
          >
            <span className="font-mono text-xs text-gray-500">{m.code_matiere}</span>
            <span>{m.nom_matiere}</span>
            <span className="text-xs text-blue-700 dark:text-blue-300 font-semibold">{m.credits} cr</span>
            {m.categorie && (
              <span className="text-[9px] uppercase px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">{m.categorie}</span>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
};

export default VueMutualisee;
